import React, { useState } from 'react';

import styles from './Login.module.css';

import FormLayout from './FormLayout';
import {
  containsLowercaseLetter,
  containsUppercaseLetter,
  containsNumber,
  containsSpecialCharacter,
  isMinLength,
  isPasswordFormatValid
} from './authHelpers';
import PasswordField from './PasswordField';
import PasswordHelper from './PasswordHelper';

interface PasswordResetFormProps {
  email: string;
  onSubmit: (confirmationCode: string, newPassword: string) => void;
  onBack: () => void;
  loading: boolean;
  errorMessage: string;
  message: string;
}

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onSubmit,
  loading,
  errorMessage,
  message
}) => {
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>('');
  const [newPasswordError, setNewPasswordError] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [meetsMinLength, setMeetsMinLength] = useState<boolean>(false);
  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);

  React.useEffect(() => {
    const match = newPassword === newPasswordConfirm;
    if (newPasswordConfirm != '' && !match) {
      setNewPasswordError('Passwords do not match.');
    } else {
      setNewPasswordError('');
    }
    setIsValid(match && isPasswordFormatValid(newPassword));
  }, [newPassword, newPasswordConfirm]);

  const handlePasswordChange = (password: string): void => {
    setNewPassword(password);
    setMeetsMinLength(isMinLength(password));
    setHasUppercase(containsUppercaseLetter(password));
    setHasLowercase(containsLowercaseLetter(password));
    setHasNumber(containsNumber(password));
    setHasSpecialChar(containsSpecialCharacter(password));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    onSubmit(confirmationCode, newPassword);
  };

  return (
    <FormLayout
      title="Password Reset"
      errorMessage={errorMessage}
      message={message}
    >
      <div style={{ paddingBottom: '12px' }}>
        A verification code has been sent to your email.
      </div>
      <form onSubmit={handleSubmit}>

        <div className={styles['form-field']}>
          <label htmlFor="confirmation-code">Verification Code:</label>
          <input
            id="confirmation-code"
            data-testid="confirmation-code-input"
            type="text"
            value={confirmationCode}
            onChange={e => setConfirmationCode(e.target.value)}
            placeholder="Enter code from email"
            required
          />
        </div>

        <PasswordField
          label="New Password:"
          onPasswordChange={(pwd: string) => handlePasswordChange(pwd)}
        />

        <PasswordHelper
          meetsMinLength={meetsMinLength}
          hasUppercase={hasUppercase}
          hasLowercase={hasLowercase}
          hasNumber={hasNumber}
          hasSpecialChar={hasSpecialChar}
        />

        <PasswordField
          label="Confirm New Password:"
          onPasswordChange={(pwd: string) => setNewPasswordConfirm(pwd)}
          showError={false}
        />
        {newPasswordError && <div className={styles['login-error-message']}>{newPasswordError}</div>}

      </form>

      <div className={styles['login-action-container']}>
        <button
          type="submit"
          className={styles['login-submit-btn']}
          data-testid="submit-btn"
          disabled={(loading || !isValid)}
          onClick={handleSubmit}
        >
            Reset Password
        </button>
      </div>

    </FormLayout>
  );
};

export default PasswordResetForm;
